import { useEffect, useMemo, useState } from 'react'
import {
  loadSchoolEmployees,
  vehicleFleetAppointAdmin,
  vehicleFleetAppointDriver,
  vehicleFleetDeactivateAdmin,
  vehicleFleetDeactivateDriver,
  vehicleFleetLoadAdmins,
  vehicleFleetLoadDrivers,
  type VehicleFleetMember,
} from '../../lib/apiPhpAdapter'
import type { User } from '../../types'
import { comparePersonNames } from '../../utils/personNameSort'

type Props = {
  kind: 'admins' | 'drivers'
  title: string
  description: string
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

function linkedUserId(e: User): string | null {
  const uid = e.user_id != null && String(e.user_id).trim() !== '' ? String(e.user_id).trim() : null
  if (uid) return uid
  const id = String(e.id || '')
  // employee-picker: linked = real user uuid; unlinked = "se-123"
  if (id && !id.startsWith('se-')) return id
  return null
}

function isStudentCandidate(e: User): boolean {
  const email = String(e.email || '').toLowerCase()
  if (email.includes('@student.')) return true
  if (e.user_type === 'student') return true
  return false
}

export function VehicleFleetAppointPanel({ kind, title, description, showAlert }: Props) {
  const [members, setMembers] = useState<VehicleFleetMember[]>([])
  const [employees, setEmployees] = useState<User[]>([])
  const [pickerId, setPickerId] = useState('')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const reload = async () => {
    const list = kind === 'admins' ? await vehicleFleetLoadAdmins() : await vehicleFleetLoadDrivers()
    setMembers(Array.isArray(list) ? list : [])
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const [list, empList] = await Promise.all([
          kind === 'admins' ? vehicleFleetLoadAdmins() : vehicleFleetLoadDrivers(),
          loadSchoolEmployees(),
        ])
        if (cancelled) return
        setMembers(Array.isArray(list) ? list : [])
        setEmployees(Array.isArray(empList) ? empList : [])
      } catch (e: unknown) {
        if (!cancelled) {
          showAlert(e instanceof Error ? e.message : 'Nepodařilo se načíst data', 'error')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [kind, showAlert])

  const activeIds = useMemo(() => {
    const s = new Set<string>()
    for (const m of members) {
      if (m.active && m.user_id) s.add(String(m.user_id))
    }
    return s
  }, [members])

  const candidates = useMemo(() => {
    return employees
      .filter((e) => {
        if (isStudentCandidate(e)) return false
        const uid = linkedUserId(e)
        if (!uid) return false
        if (activeIds.has(uid)) return false
        if (!String(e.personal_number || '').trim()) return false
        return true
      })
      .filter((e) => {
        if (!search.trim()) return true
        const n = search.trim().toLowerCase()
        return (
          (e.name || '').toLowerCase().includes(n) ||
          (e.email || '').toLowerCase().includes(n) ||
          String(e.personal_number || '')
            .toLowerCase()
            .includes(n)
        )
      })
      .sort(comparePersonNames)
  }, [employees, activeIds, search])

  const activeMembers = members.filter((m) => !!m.active)

  return (
    <section
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: '1.25rem 1.4rem',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
        maxWidth: 720,
      }}
    >
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      <p style={{ marginBottom: 14, color: '#64748b', lineHeight: 1.45 }}>{description}</p>

      {loading ? (
        <p>Načítání…</p>
      ) : (
        <>
          <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
            <input
              type="search"
              placeholder="Hledat zaměstnance…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: '0.45rem 0.55rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select
                value={pickerId}
                onChange={(e) => setPickerId(e.target.value)}
                disabled={busy}
                style={{
                  flex: '1 1 220px',
                  padding: '0.45rem 0.55rem',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                }}
              >
                <option value="">— Vyberte zaměstnance —</option>
                {candidates.map((e) => {
                  const uid = linkedUserId(e)!
                  return (
                    <option key={uid} value={uid}>
                      {e.name}
                      {e.personal_number ? ` · ${e.personal_number}` : ''}
                      {e.email ? ` (${e.email})` : ''}
                    </option>
                  )
                })}
              </select>
              <button
                type="button"
                className="system-button"
                disabled={busy || !pickerId}
                onClick={() => {
                  void (async () => {
                    if (!pickerId) return
                    setBusy(true)
                    try {
                      if (kind === 'admins') {
                        await vehicleFleetAppointAdmin(pickerId)
                      } else {
                        await vehicleFleetAppointDriver(pickerId)
                      }
                      setPickerId('')
                      await reload()
                      showAlert('Uloženo', 'success')
                    } catch (err: unknown) {
                      showAlert(err instanceof Error ? err.message : 'Uložení selhalo', 'error')
                    } finally {
                      setBusy(false)
                    }
                  })()
                }}
              >
                Jmenovat
              </button>
            </div>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8' }}>
              Jen zaměstnanci školy s osobním číslem a propojeným intranet účtem (bez studentů).
            </p>
          </div>

          <h3 style={{ fontSize: '1rem', marginBottom: 8 }}>Aktivní ({activeMembers.length})</h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.25rem' }}>
            {activeMembers.map((m) => (
              <li
                key={m.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  alignItems: 'center',
                  padding: '0.55rem 0',
                  borderBottom: '1px solid #e2e8f0',
                }}
              >
                <div>
                  <strong>{m.display_name || m.user_name || m.user_id}</strong>
                  {m.email ? (
                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{m.email}</div>
                  ) : null}
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    void (async () => {
                      setBusy(true)
                      try {
                        if (kind === 'admins') {
                          await vehicleFleetDeactivateAdmin(m.id)
                        } else {
                          await vehicleFleetDeactivateDriver(m.id)
                        }
                        await reload()
                        showAlert('Odebráno', 'success')
                      } catch (err: unknown) {
                        showAlert(err instanceof Error ? err.message : 'Odebrání selhalo', 'error')
                      } finally {
                        setBusy(false)
                      }
                    })()
                  }}
                >
                  Odebrat
                </button>
              </li>
            ))}
            {activeMembers.length === 0 ? (
              <li style={{ color: '#64748b', padding: '0.5rem 0' }}>Zatím nikdo není jmenován.</li>
            ) : null}
          </ul>
        </>
      )}
    </section>
  )
}
